import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, Wrench, Cog, FileText,
  LogOut, Menu, X, Layers
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Mientras se verifica la sesion: spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  // Sin sesion -> redirigir al login conservando la URL destino
  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/familias', label: 'Familias', icon: Layers },
    { path: '/admin/productos', label: 'Productos', icon: Package },
    { path: '/admin/servicios', label: 'Servicios', icon: Wrench },
    { path: '/admin/procesos', label: 'Procesos', icon: Cog },
    { path: '/admin/cotizaciones', label: 'Cotizaciones', icon: FileText },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col bg-steel-dark text-white transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        {/* Logo */}
        <div className="p-4 border-b border-steel-gray flex items-center justify-between">
          {sidebarOpen && (
            <img 
              src="https://nayaceros.com/images/logo.png" 
              alt="NAYACEROS" 
              className="h-10"
            />
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-steel-charcoal rounded"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 mx-2 rounded transition-colors ${
                isActive(item.path, item.exact) 
                  ? 'bg-accent-primary text-white' 
                  : 'text-text-light hover:bg-steel-charcoal'
              }`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-steel-gray">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-text-light">{user?.email}</p>
              </div>
              <button onClick={handleLogout} className="p-2 hover:bg-steel-charcoal rounded text-accent-primary">
                <LogOut size={20} />
              </button>
            </div>
          ) : (
            <button onClick={handleLogout} className="p-2 hover:bg-steel-charcoal rounded text-accent-primary mx-auto block">
              <LogOut size={20} />
            </button>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-steel-dark text-white z-50 px-4 py-3 flex items-center justify-between">
        <img 
          src="https://nayaceros.com/images/logo.png" 
          alt="NAYACEROS" 
          className="h-8"
        />
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-steel-dark z-40 pt-16">
          <nav className="p-4">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded mb-1 ${
                  isActive(item.path, item.exact) 
                    ? 'bg-accent-primary text-white' 
                    : 'text-text-light'
                }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </Link>
            ))}
            <button 
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-accent-primary w-full mt-4"
            >
              <LogOut size={20} />
              <span>Cerrar Sesión</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:pt-0 pt-16 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
